import DownloadIcon from '@mui/icons-material/Download';
import HighQualityOutlinedIcon from '@mui/icons-material/HighQualityOutlined';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { SxProps } from '@mui/material/styles';
import React from 'react';
import { useAppSelector } from '../../app/hooks';
import { useBlock } from '../../services/entities/hooks/useBlock';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { flexCenter } from '../commonUI/cssClasses';
import {
  selectCurrBlockID,
  selectCurrFeature,
  selectCurrPointId,
} from '../select/selectSlice';
import ImageNav from './ImageNav';
import MaskToggle from './MaskToggle';
import { SettingsButton } from './SettingsButton';
import { useOriginalImageDownloader } from './hooks/useOriginalImageDownloader';
import {
  selectBackendColorCorrection,
  selectCameraSelection,
  selectFrontendColorCorrection,
  selectImageResolution,
} from './imageSlice';
import { useImageContext } from './providers/ImageProvider';
import { getImageUrl } from './utils/imageUtils';
const ImageToolbox: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const currBlockID = useAppSelector(selectCurrBlockID);
  const currPointID = useAppSelector(selectCurrPointId);
  const currFeature = useAppSelector(selectCurrFeature);
  const currImageRes = useAppSelector(selectImageResolution);
  const { block } = useBlock(currBlockID);
  const imageContext = useImageContext();
  const cameraSelection = useAppSelector(selectCameraSelection);
  const backendColorCorrection = useAppSelector(selectBackendColorCorrection);
  const frontendColorCorrection = useAppSelector(selectFrontendColorCorrection);

  const { downloadOriginalCorrectedImage } = useOriginalImageDownloader();

  const commonLabelStyles: SxProps = {
    ...flexCenter,
    color: theme.colors.common.softWhite,
    p: '0 0.5rem',
    whiteSpace: 'nowrap',
    fontSize: {
      sm: '0.6rem',
      md: '0.8rem',
      lg: '1rem',
    },
  };

  const commonToolboxStyles: SxProps = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.darkTheme.grey.medium,
    borderRadius: theme.borderRadius.primary,
    p: '0 0.5rem',
  };

  const handleDownloadClick = () => {
    imageContext.downloadImageFromStage(
      `${block?.name}-${currPointID}-${t(currFeature, {
        ns: 'common',
      })}-${currImageRes}p.png`
    );
  };

  const handleDownloadOriginalClick = () => {
    if (block && currPointID && currFeature && imageContext.currPoint) {
      const cdnURI = getImageUrl(
        imageContext.currPoint,
        cameraSelection,
        backendColorCorrection,
        { format: 'jpg' }
      );
      const fileName = `${block.name}-${currPointID}-${t(currFeature, {
        ns: 'common',
      })}-Original${frontendColorCorrection ? '-Color_Corrected' : ''}.png`;
      downloadOriginalCorrectedImage(cdnURI, fileName);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        p: '0.3rem',
        bgcolor: theme.colors.darkTheme.grey.medium,
        color: theme.colors.common.white,
        ...flexCenter,
      }}
    >
      <Box sx={{ ...commonToolboxStyles, flexDirection: 'row' }}>
        <Tooltip
          title={t('Download image with current quality and masks', {
            ns: 'ranch-view',
          })}
        >
          <IconButton onClick={handleDownloadClick}>
            <DownloadIcon
              sx={{
                fill: (theme) => theme.colors.common.white,
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ ...commonToolboxStyles, flexDirection: 'row' }}>
        <Tooltip
          title={t('Download image with highest quality', { ns: 'ranch-view' })}
        >
          <IconButton onClick={handleDownloadOriginalClick}>
            <HighQualityOutlinedIcon
              sx={{
                fill: (theme) => theme.colors.common.white,
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ ...commonToolboxStyles, flexDirection: 'row' }}>
        <SettingsButton />
      </Box>
      <Box sx={{ ...commonToolboxStyles, flexDirection: 'row' }}>
        <Typography variant='subtitle1' sx={{ ...commonLabelStyles }}>
          {t('masks', { ns: 'ranch-view' })}
        </Typography>
        <MaskToggle />
      </Box>
      <Box sx={{ ...commonToolboxStyles, flexDirection: 'row' }}>
        <ImageNav />
      </Box>
    </Box>
  );
};

export default ImageToolbox;
